import { NumberInput, Tooltip } from "@mantine/core";
import { MinusIcon, PlusIcon } from "@heroicons/react/16/solid";
import styled from "styled-components";
import { theme } from "../../../theme";

/**
 * Sayı artır/azalt kutusu (rapor ekranlarındaki tolerans alanları).
 *
 * Eski hâli yeşil (#3db772) kare butonlar + PNG ikonlardı; panelin geri kalanı
 * mor/slate paletini ve heroicons setini kullandığı için tek başına duruyordu.
 * Artık tek parça, 44px yüksekliğinde, diğer inputlarla aynı kenarlık ve köşe
 * yarıçapına sahip bir grup.
 */
const CountingButton = ({
  value,
  increment,
  decrement,
  setValue,
  label,
  description,
  min = 0,
  w = 132,
}: any) => {
  return (
    <Field style={{ width: w }}>
      {label &&
        (description ? (
          <Tooltip label={description} withArrow multiline w={240}>
            <FieldLabel $hasHelp>{label}</FieldLabel>
          </Tooltip>
        ) : (
          <FieldLabel>{label}</FieldLabel>
        ))}
      <Group>
        <StepButton
          type="button"
          onClick={decrement}
          aria-label={`${label ?? "Değer"} azalt`}
          disabled={value <= min}
        >
          <MinusIcon width={16} />
        </StepButton>
        <StyledInput
          value={value}
          // NumberInput boş bırakıldığında `""` döndürüyor; artır/azalt bunu
          // metin olarak toplayıp değeri bozmasın diye sayıya çevriliyor.
          onChange={(next: string | number) => setValue(Number(next) || min)}
          min={min}
          clampBehavior="strict"
          hideControls
          aria-label={label}
        />
        <StepButton
          type="button"
          onClick={increment}
          aria-label={`${label ?? "Değer"} artır`}
        >
          <PlusIcon width={16} />
        </StepButton>
      </Group>
    </Field>
  );
};

export default CountingButton;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label<{ $hasHelp?: boolean }>`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #64748b;
  white-space: nowrap;
  ${({ $hasHelp }) => $hasHelp && "cursor: help; border-bottom: 1px dotted #cbd5e1; align-self: flex-start;"}
`;

const Group = styled.div`
  display: flex;
  align-items: stretch;
  height: 44px;
  border: 1px solid ${theme.colors.greyscale.gray40};
  border-radius: 10px;
  background: #ffffff;
  overflow: hidden;

  &:focus-within {
    border-color: ${theme.colors.brand.normal};
    box-shadow: 0px 0px 5px 3px #5c26ff14;
  }
`;

const StepButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  flex-shrink: 0;
  border: none;
  background: #f8fafc;
  color: ${theme.colors.brand.normal};
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: #f1eaff;
  }
  &:disabled {
    color: ${theme.colors.greyscale.gray60};
    cursor: not-allowed;
  }
`;

// Global stil tüm .mantine-Input-input'lara 44px yükseklik ve kenarlık veriyor;
// grup içindeki alan için ikisi de geri alınıyor.
const StyledInput = styled(NumberInput)`
  flex: 1;
  min-width: 0;

  .mantine-NumberInput-wrapper,
  .mantine-Input-wrapper {
    height: 100%;
  }

  .mantine-NumberInput-input {
    height: 100% !important;
    min-height: 100%;
    text-align: center;
    border: none;
    border-radius: 0;
    border-left: 1px solid ${theme.colors.greyscale.gray20};
    border-right: 1px solid ${theme.colors.greyscale.gray20};
    font-size: 15px;
    font-weight: 500;
    color: ${theme.colors.greyscale.black};

    &:focus {
      border-color: ${theme.colors.greyscale.gray20};
      box-shadow: none;
    }
  }
`;
